import React from 'react'

// Hooks
import { useUpdateShareParentPermissions } from 'modules/shares/modals/ShareSettingsModal/ParentPermissions/useUpdateShareParentPermissions'

// Components
import { Banner, Text, Box } from '@sketch/components'
import { ParentAccessLevel } from '@sketch/modules-common'
import InvitePeople from 'modules/shares/modals/ShareSettingsModal/InvitePeople'
import ShareMemberships from 'modules/shares/modals/ShareSettingsModal/ShareMemberships'

// Styles
import { StyledRadioButton } from './SharingModal.styles'

// Types
import { ShareWithoutVersion } from 'modules/versioning'

export const WorkspaceMembersTitle = ({
  share,
}: {
  share: Pick<ShareWithoutVersion, 'project' | 'parentAccessLevel'>
}) => {
  const viewOnly = share.parentAccessLevel === 'VIEW'
  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'
  const restricted = viewOnly || inRestrictedProject

  return <>Workspace Members ({restricted ? 'Restricted' : 'All'})</>
}

export const RestrictedProjectCallout = ({
  share,
}: {
  share: Pick<ShareWithoutVersion, 'project' | 'isDraft'>
}) => {
  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'
  const isDraft = share.project?.type === 'PERSONAL_DRAFTS'

  if (!inRestrictedProject) {
    return null
  }

  const calloutCopy = isDraft
    ? 'You can share documents in My Drafts with specific people, but not the whole Workspace.'
    : 'This document is in a restricted project. Changing access options will only affect current project Members.'

  return (
    <Box mb={6}>
      <Banner
        type="information"
        data-testid="sharing-workspace-restricted-banner"
      >
        {calloutCopy}
      </Banner>
    </Box>
  )
}

export const WorkspaceMembersPanel = ({
  share,
}: {
  share: ShareWithoutVersion
}) => {
  const {
    getUpdateShareParentPermissionsDropdownProps,
    shareParentAccessLevel,
  } = useUpdateShareParentPermissions({ share })

  const { setLevel } = getUpdateShareParentPermissionsDropdownProps()

  const viewOnly = shareParentAccessLevel === 'VIEW'
  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'
  const showInviteWorkspaceMembersInput = viewOnly || inRestrictedProject

  const workspaceName = share.workspace.name

  const handleAccessLevelChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLevel(e.target.value as ParentAccessLevel)

  const isDraft = !!(share.isDraft ?? share.project?.type === 'PERSONAL_DRAFTS')

  const shareWithIsDraft = { ...share, isDraft }

  return (
    <>
      <RestrictedProjectCallout share={share} />

      {!isDraft && (
        <>
          <Text textStyle="copy.primary.standard.F">
            Members of “<strong>{workspaceName}</strong>” can:
          </Text>

          <StyledRadioButton
            name="accessLevel"
            label="Edit or view this document"
            checked={!viewOnly}
            value="DEFAULT"
            onChange={handleAccessLevelChange}
          />
          <StyledRadioButton
            name="accessLevel"
            label="View this document on the web app"
            checked={viewOnly}
            value="VIEW"
            onChange={handleAccessLevelChange}
          />
        </>
      )}

      {showInviteWorkspaceMembersInput && (
        <>
          <Text textStyle="copy.primary.standard.F" pt={6}>
            Invite other “<strong>{workspaceName}</strong>” Members:
          </Text>
          <InvitePeople
            data-testid="sharing-workspace-invite"
            hideLabel
            share={share}
            filterMembers="Members"
          />
          <ShareMemberships share={shareWithIsDraft} />
        </>
      )}
    </>
  )
}
